import { useMemo, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '@/lib/api';
import { useApiData } from '@/lib/useApi';
import { useAuth } from '@/auth/AuthContext';
import type { LeadRow, UserRow } from '@/lib/types';
import { initials, fullName, formatDate } from '@/lib/format';

const STATUSES = ['new', 'contacted', 'interested', 'follow-up', 'converted', 'lost'];
const statusStyle: Record<string, string> = {
  new: 'bg-blue-50 text-blue-700', contacted: 'bg-amber-50 text-amber-700',
  interested: 'bg-green-50 text-green-700', 'follow-up': 'bg-purple-50 text-purple-700',
  converted: 'bg-teal-50 text-teal-700', lost: 'bg-red-50 text-red-600',
};
const title = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export default function LeadsPage() {
  const { profile } = useAuth();
  const role = profile?.role ?? '';
  const canAssign = role === 'Owner' || role === 'Manager';
  const isCounselor = role === 'Counselor';

  const { data: leadData, loading, reload } = useApiData<LeadRow[]>(() => api.leads());
  const { data: userData } = useApiData<UserRow[]>(() => api.users());

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [counselorFilter, setCounselorFilter] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);

  const leads = useMemo(() => leadData ?? [], [leadData]);
  const users = useMemo(() => userData ?? [], [userData]);
  // Counselors are the assignable pool; owners/managers can also hold leads, so allow all active users.
  const assignable = users.filter((u) => u.status === 'active');
  const nameById = useMemo(() => new Map(users.map((u) => [u.id, u.fullName])), [users]);

  const filtered = useMemo(() => leads.filter((l) => {
    const name = `${l.first_name} ${l.last_name ?? ''} ${l.phone}`.toLowerCase();
    if (search && !name.includes(search.toLowerCase())) return false;
    if (statusFilter && (l.status || '').toLowerCase() !== statusFilter) return false;
    if (counselorFilter === 'unassigned' && l.assigned_counselor_id) return false;
    if (counselorFilter && counselorFilter !== 'unassigned' && String(l.assigned_counselor_id ?? '') !== counselorFilter) return false;
    return true;
  }), [leads, search, statusFilter, counselorFilter]);

  const total = leads.length;
  const unassigned = leads.filter((l) => !l.assigned_counselor_id).length;
  const converted = leads.filter((l) => (l.status || '').toLowerCase() === 'converted').length;
  const newCount = leads.filter((l) => (l.status || '').toLowerCase() === 'new').length;

  const assign = async (lead: LeadRow, counselorId: string) => {
    setBusyId(lead.id);
    try { await api.updateLead(lead.id, { assignedCounselorId: counselorId ? Number(counselorId) : null }); await reload(); }
    catch { /* ignore */ } finally { setBusyId(null); }
  };
  const changeStatus = async (lead: LeadRow, status: string) => {
    setBusyId(lead.id);
    try { await api.updateLead(lead.id, { status }); await reload(); }
    catch { /* ignore */ } finally { setBusyId(null); }
  };

  const kpis: [string, number, string, string][] = isCounselor
    ? [['My Leads', total, 'ri-focus-3-line', 'bg-[#0B6E77]'], ['New', newCount, 'ri-fire-line', 'bg-sky-500'], ['Converted', converted, 'ri-checkbox-circle-line', 'bg-green-600'], ['Follow-ups', leads.filter((l) => (l.status || '').toLowerCase() === 'follow-up').length, 'ri-time-line', 'bg-amber-500']]
    : [['Total App Leads', total, 'ri-focus-3-line', 'bg-[#0B6E77]'], ['Unassigned', unassigned, 'ri-user-unfollow-line', 'bg-amber-500'], ['New', newCount, 'ri-fire-line', 'bg-sky-500'], ['Converted', converted, 'ri-checkbox-circle-line', 'bg-green-600']];

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <div>
          <h2 className="text-xl font-bold text-[#3C3C3C]">{isCounselor ? 'My Leads' : 'App Leads'}</h2>
          <p className="text-sm text-gray-500 mt-1">{isCounselor
            ? 'Leads assigned to you. Update their status as you work them.'
            : 'Leads coming in from the student app. Assign each one to a counselor to follow up.'}</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {kpis.map(([label, value, icon, bg]) => (
            <div key={label} className={`${bg} rounded-xl p-4 text-white flex items-center justify-between`}>
              <div><p className="text-2xl font-bold">{loading ? '—' : value}</p><p className="text-xs text-white/80 mt-0.5">{label}</p></div>
              <i className={`${icon} text-2xl text-white/70`} />
            </div>
          ))}
        </div>

        <div className="bg-white border border-[#E1E5EA] rounded-xl overflow-hidden">
          <div className="p-4 border-b border-[#E1E5EA] flex flex-col md:flex-row gap-3">
            <div className="relative flex-1"><i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or phone…" className="w-full pl-10 pr-4 py-2.5 border border-[#E1E5EA] rounded-lg text-sm outline-none focus:border-[#0B6E77]" /></div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-[#E1E5EA] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#0B6E77]"><option value="">All statuses</option>{STATUSES.map((s) => <option key={s} value={s}>{title(s)}</option>)}</select>
            {canAssign && <select value={counselorFilter} onChange={(e) => setCounselorFilter(e.target.value)} className="border border-[#E1E5EA] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#0B6E77]"><option value="">All counselors</option><option value="unassigned">Unassigned</option>{assignable.map((u) => <option key={u.id} value={u.id}>{u.fullName}</option>)}</select>}
          </div>

          {loading ? <div className="p-12 text-center text-sm text-gray-400">Loading…</div>
            : filtered.length === 0 ? (
              <div className="p-16 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-gray-50 flex items-center justify-center text-gray-300 mb-3"><i className="ri-smartphone-line text-2xl" /></div>
                <p className="text-sm font-medium text-gray-600">{leads.length === 0 ? 'No app leads yet' : 'No leads match your filters'}</p>
                <p className="text-xs text-gray-400 mt-1 max-w-sm mx-auto">{leads.length === 0
                  ? (isCounselor ? 'Leads assigned to you by your manager will appear here.' : 'Leads from the student app will land here automatically. Assign them to counselors as they arrive.')
                  : 'Try clearing the search or filters.'}</p>
              </div>
            ) : (
              <div className="overflow-x-auto"><table className="w-full min-w-[860px]">
                <thead><tr className="bg-gray-50 text-left text-[11px] uppercase text-gray-400">
                  <th className="px-4 py-3">Lead</th><th className="px-4 py-3">Interest</th><th className="px-4 py-3">Source</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">{isCounselor ? 'Follow-up' : 'Assigned to'}</th><th className="px-4 py-3"></th>
                </tr></thead>
                <tbody className="divide-y divide-gray-50">{filtered.map((l) => {
                  const name = fullName(l.first_name, l.last_name);
                  return (
                    <tr key={l.id} className={`hover:bg-gray-50 ${busyId === l.id ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-3"><div className="flex items-center gap-3"><div className="w-9 h-9 bg-[#0B6E77] text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0">{initials(name)}</div><div><p className="text-sm font-semibold text-gray-800">{name}</p><p className="text-[11px] text-gray-400">{l.phone}</p></div></div></td>
                      <td className="px-4 py-3 text-sm text-gray-600">{l.destination_country || '—'}</td>
                      <td className="px-4 py-3"><span className="text-xs bg-sky-50 text-sky-700 px-2 py-1 rounded-full">{l.source || 'App'}</span></td>
                      <td className="px-4 py-3">
                        <select value={(l.status || 'new').toLowerCase()} onChange={(e) => changeStatus(l, e.target.value)} className={`text-xs font-medium rounded-full px-2.5 py-1 border-0 outline-none cursor-pointer ${statusStyle[(l.status || 'new').toLowerCase()] ?? 'bg-gray-50 text-gray-500'}`}>{STATUSES.map((s) => <option key={s} value={s}>{title(s)}</option>)}</select>
                      </td>
                      <td className="px-4 py-3">
                        {canAssign
                          ? <select value={l.assigned_counselor_id ?? ''} onChange={(e) => assign(l, e.target.value)} className="text-sm border border-[#E1E5EA] rounded-lg px-2 py-1.5 outline-none focus:border-[#0B6E77] cursor-pointer max-w-[160px]"><option value="">Unassigned</option>{assignable.map((u) => <option key={u.id} value={u.id}>{u.fullName}</option>)}</select>
                          : isCounselor ? <span className="text-sm text-gray-500">{l.next_follow_up_at ? formatDate(l.next_follow_up_at) : '—'}</span>
                          : <span className="text-sm text-gray-600">{l.assigned_counselor_id ? (nameById.get(l.assigned_counselor_id) ?? '—') : <span className="text-amber-600">Unassigned</span>}</span>}
                      </td>
                      <td className="px-4 py-3 text-right"><a href={`tel:${l.phone}`} className="inline-flex items-center gap-1 text-sm text-[#0B6E77] font-medium hover:underline"><i className="ri-phone-line" />Call</a></td>
                    </tr>
                  );
                })}</tbody>
              </table></div>
            )}
        </div>
      </div>
    </DashboardLayout>
  );
}
